import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X, Plus, Box, CalendarClock } from 'lucide-react';

/**
 * HeroRotationManager — gerencia a rotação semanal automática da vitrine 3D.
 * Props:
 *   - allProducts3D: [{id, name, ...}] — apenas produtos com model_3d_url
 *   - rotation: [productId, ...] — ordem atual do rodízio (semana ISO % length)
 *   - onChange: (novaRotationArray) => void
 *   - currentEffectiveId: id do produto exibido nesta semana (para destaque visual)
 */
const RotationRow = ({ product, index, isCurrent, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: product._rotationKey,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`rotation-row-${index}`}
      className={`flex items-center gap-3 border p-3 bg-white transition-colors ${
        isCurrent ? 'border-amber-500 bg-amber-50/40' : 'border-border'
      }`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-primary touch-none"
        aria-label="Arrastar para reordenar"
      >
        <GripVertical className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <div className="w-9 h-9 flex items-center justify-center bg-neutral-50 border border-border shrink-0">
        {product.images?.[0] ? (
          <img src={product.images[0]} alt="" className="w-full h-full object-contain" />
        ) : (
          <Box className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-2">#{index + 1}</span>
          {product.name}
        </p>
        {isCurrent && (
          <p className="text-[10px] uppercase tracking-widest text-amber-600 flex items-center gap-1">
            <CalendarClock className="w-3 h-3" strokeWidth={1.5} /> Exibido esta semana
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        data-testid={`rotation-remove-${index}`}
        className="text-muted-foreground hover:text-destructive p-1"
        title="Remover"
      >
        <X className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </div>
  );
};

const HeroRotationManager = ({ allProducts3D = [], rotation = [], onChange, currentEffectiveId }) => {
  const [picking, setPicking] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Anota cada slot com uma chave estável (permite repetir mesmo produto sem colisão)
  const rows = rotation
    .map((id, idx) => {
      const p = allProducts3D.find((x) => x.id === id);
      if (!p) return null;
      return { ...p, _rotationKey: `${id}-${idx}` };
    })
    .filter(Boolean);

  const notInRotation = allProducts3D.filter((p) => !rotation.includes(p.id));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rows.findIndex((r) => r._rotationKey === active.id);
    const newIndex = rows.findIndex((r) => r._rotationKey === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(rows, oldIndex, newIndex).map((r) => r.id);
    onChange(reordered);
  };

  const handleRemove = (idx) => {
    onChange(rotation.filter((_, i) => i !== idx));
  };

  const handleAdd = (productId) => {
    onChange([...rotation, productId]);
    setPicking(false);
  };

  return (
    <div className="pt-6 mt-6 border-t border-border">
      <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-3 block flex items-center gap-2">
        <CalendarClock className="w-4 h-4" strokeWidth={1.5} />
        Rodízio Semanal Automático (opcional)
      </label>
      <p className="text-xs text-muted-foreground mb-4 italic">
        Configure uma fila de peças para a Vitrine 3D. O sistema troca automaticamente por semana ISO
        (mesma peça exibida a cada segunda-feira até domingo). Se a fila estiver vazia, cai no produto
        escolhido acima. Arraste para reordenar.
      </p>

      {rows.length === 0 ? (
        <div
          data-testid="rotation-empty"
          className="text-center py-8 border border-dashed border-border text-sm text-muted-foreground"
        >
          Nenhuma peça na fila. Adicione produtos com modelo 3D abaixo.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={rows.map((r) => r._rotationKey)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2" data-testid="rotation-list">
              {rows.map((product, idx) => (
                <RotationRow
                  key={product._rotationKey}
                  product={product}
                  index={idx}
                  isCurrent={product.id === currentEffectiveId && rotation.length > 0}
                  onRemove={() => handleRemove(idx)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <div className="mt-4">
        {picking ? (
          <div className="border border-border p-4 bg-neutral-50">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
              Escolher peça para adicionar
            </p>
            {notInRotation.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                Todos os produtos 3D já estão na fila. Você pode remover algum acima para trocar.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {notInRotation.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => handleAdd(p.id)}
                    data-testid={`rotation-add-${p.id}`}
                    className="flex items-center gap-3 border border-border bg-white hover:border-primary p-2 text-left transition-colors"
                  >
                    <div className="w-9 h-9 flex items-center justify-center bg-neutral-50 border border-border shrink-0">
                      {p.images?.[0] ? (
                        <img src={p.images[0]} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <Box className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                      )}
                    </div>
                    <span className="text-sm truncate flex-1">{p.name}</span>
                    <Plus className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => setPicking(false)}
              className="mt-3 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary"
            >
              Cancelar
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setPicking(true)}
            data-testid="rotation-add-button"
            disabled={notInRotation.length === 0 && rows.length >= allProducts3D.length}
            className="flex items-center gap-2 border border-dashed border-border hover:border-primary bg-white px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-40"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} />
            Adicionar peça à fila
          </button>
        )}
      </div>
    </div>
  );
};

export default HeroRotationManager;
